<template>
  <div v-if="pa && pa.activeSku">
    <div class="section">
      <div class="section-head">
        <div class="section-title">
          销售信息
          <small>销售规格与基础属性</small>
        </div>
      </div>
      <a-row :gutter="24">
        <a-col :span="12">
          <a-form-model-item label="SKU编码">
            <a-input v-model="pa.activeSku.skuCode" placeholder="系统保存后自动生成，支持手动修改" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12" />
        <a-col :span="12">
          <a-form-model-item label="规格名称" required>
            <a-input v-model="pa.activeSku.skuName" placeholder="请输入规格名称" />
          </a-form-model-item>
        </a-col>
        <a-col :span="24">
          <div class="section-title" style="margin:4px 0 12px">单位配置 <small>基本单位与左侧商品信息同步；库存仍按规格原核算单位记账</small></div>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="基本单位" required>
            <a-select
              :key="'sku-base-unit-' + pa.activeSkuIndex + '-' + pa.skuBaseUnitSelectValue(pa.activeSku)"
              :value="pa.skuBaseUnitSelectValue(pa.activeSku)"
              placeholder="请选择"
              @change="v => pa.onConfigBaseChange(pa.activeSku, v)">
              <a-select-option v-for="o in pa.unitOptions" :key="o" :value="o">{{ o }}</a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="整箱单位" :required="pa.activeSku.saleUnitMode === 'case'">
            <a-select
              v-model="pa.activeSku.caseUnitName"
              placeholder="默认与基本单位相同"
              @change="v => pa.onCaseUnitChange(pa.activeSku, v)">
              <a-select-option v-for="o in pa.unitOptions" :key="'case-' + o" :value="o">{{ o }}</a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="销售单位" required>
            <a-radio-group :value="pa.activeSku.saleUnitMode || 'base'" @change="e => pa.onSaleModeChange(pa.activeSku, e)">
              <a-radio value="base">基本单位</a-radio>
              <a-radio value="case">整箱单位</a-radio>
            </a-radio-group>
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="箱规" :required="pa.activeSku.saleUnitMode === 'case'">
            <a-input-number
              v-model="pa.activeSku.caseRate"
              :min="0.000001"
              :precision="6"
              style="width:100%"
              placeholder="默认 1" />
            <div class="help">1 整箱单位 = 箱规 × 基本单位。默认 1，即 1:1。</div>
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="最小下单数" required>
            <a-input-number v-model="pa.activeSku.minQty" :min="0" style="width:100%" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="毛重(kg)">
            <a-input-number v-model="pa.activeSku.grossWeight" :min="0" :precision="3" style="width: 100%" placeholder="请输入毛重" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="净重(kg)">
            <a-input-number v-model="pa.activeSku.netWeight" :min="0" :precision="3" style="width: 100%" placeholder="请输入净重" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="体积(m³)">
            <a-input-number v-model="pa.activeSku.volume" :min="0" :precision="3" style="width: 100%" placeholder="请输入体积" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="损耗比例">
            <div style="display:flex;align-items:center;gap:6px">
              <a-input-number v-model="pa.activeSku.lossRate" :min="0" :precision="1" style="flex:1" />
              <span>%</span>
            </div>
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="是否称重">
            <a-radio-group v-model="pa.activeSku.weigh">
              <a-radio value="是">是</a-radio>
              <a-radio value="否">否</a-radio>
            </a-radio-group>
          </a-form-model-item>
        </a-col>
      </a-row>
    </div>

    <div class="section">
      <div class="section-head">
        <div class="section-title">
          更多设置
          <small>规格编码、状态、区域、图片及商品描述</small>
        </div>
      </div>
      <a-row :gutter="24">
        <a-col :span="12">
          <a-form-model-item label="分拣分包">
            <a-switch
              :checked="pa.activeSku.sorting === '开启'"
              checked-children="开"
              un-checked-children="关"
              @change="v => { pa.activeSku.sorting = v ? '开启' : '关闭' }"
            />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="区域">
            <a-input v-model="pa.activeSku.region" placeholder="请输入区域" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="商品图片">
            <product-attachment-upload v-model="pa.activeSku.image" kind="image" />
            <div class="help">默认继承商品主图，规格可单独维护。</div>
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="产地">
            <a-input v-model="pa.activeSku.origin" placeholder="请输入产地" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="描述" :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }">
            <a-textarea v-model="pa.activeSku.skuDesc" :rows="2" placeholder="规格描述" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="商品规格">
            <a-textarea v-model="pa.activeSku.specDesc" :rows="2" placeholder="如：1斤/斤（约500g~600g/斤）" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="商品特征">
            <a-textarea v-model="pa.activeSku.feature" :rows="2" placeholder="规格级商品特征" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="售后标准">
            <a-textarea v-model="pa.activeSku.afterSale" :rows="2" placeholder="规格级售后标准" />
          </a-form-model-item>
        </a-col>
      </a-row>
    </div>

    <div class="section">
      <div class="section-head">
        <div class="section-title">
          供应链信息
          <small>包装、采购说明与库存规则</small>
        </div>
      </div>
      <a-row :gutter="24">
        <a-col :span="12">
          <a-form-model-item label="装箱类型">
            <a-select v-model="pa.activeSku.packType" placeholder="请选择">
              <a-select-option v-for="item in pa.packOptions" :key="item" :value="item">{{ item }}</a-select-option>
            </a-select>
          </a-form-model-item>
        </a-col>
        <a-col :span="24">
          <a-form-model-item label="采购描述" :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
            <a-textarea v-model="pa.activeSku.purchaseDesc" :rows="2" placeholder="请输入采购说明" />
          </a-form-model-item>
        </a-col>
        <a-col :span="12">
          <a-form-model-item label="库存设置" required>
            <a-radio-group v-model="pa.activeSku.stockMode">
              <a-radio value="虚拟库存">虚拟库存</a-radio>
              <a-radio value="实际库存">实际库存</a-radio>
              <a-radio value="设置库存">设置库存</a-radio>
            </a-radio-group>
          </a-form-model-item>
        </a-col>
        <a-col v-if="pa.activeSku.stockMode === '设置库存'" :span="12">
          <a-form-model-item label="库存数量">
            <div style="display:flex;align-items:center;gap:6px">
              <a-input-number v-model="pa.activeSku.stockValue" :min="0" style="flex:1" />
              <span style="white-space:nowrap;color:#8792a5">{{ pa.skuSaleUnitLabel }}</span>
            </div>
          </a-form-model-item>
        </a-col>
      </a-row>
    </div>

    <div class="section">
      <div class="section-head">
        <div class="section-title">
          备注
          <small>规格级补充说明</small>
        </div>
      </div>
      <a-row :gutter="24">
        <a-col :span="24">
          <a-form-model-item label="备注" :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
            <a-textarea v-model="pa.activeSku.remark" :rows="4" placeholder="请输入该规格的补充说明或业务备注" />
          </a-form-model-item>
        </a-col>
      </a-row>
    </div>

    <section class="section base-info-group">
      <div class="section-head base-info-group-head">
        <div class="section-title">仓库信息 <small>按当前规格维护采购属性、采购方式、供应商及供应成本</small></div>
        <a-button :disabled="!pa.canAddWarehouse" :title="pa.warehouseAddHint" @click="pa.addWarehouse">+ 添加仓库</a-button>
      </div>
      <div class="base-info-card">
        <component
          :is="WarehouseTable"
          v-if="WarehouseTable"
          :rows="pa.form.warehouses"
          :warehouse-options="pa.warehouseRecords"
          :supplier-options="pa.supplierRecords"
          @remove="pa.removeWarehouse" />
      </div>
    </section>
    <section class="section base-info-group">
      <div class="section-head base-info-group-head">
        <div class="section-title">商品MOQ设置 <small>按当前规格维护最低采购限制，可按数量或金额控制</small></div>
        <a-button @click="pa.openMoqModal">+ 添加MOQ规则</a-button>
      </div>
      <div class="base-info-card">
        <component :is="MoqTable" v-if="MoqTable" :rows="pa.form.moqs" @remove="pa.removeMoq" />
      </div>
    </section>
  </div>
</template>

<script>
import ProductAttachmentUpload from './ProductAttachmentUpload.vue'

export default {
  name: 'ProductSkuSpecSections',
  components: { ProductAttachmentUpload },
  inject: {
    pa: { from: 'productArchiveVm', default: null },
    WarehouseTable: { default: null },
    MoqTable: { default: null }
  }
}
</script>
